import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface AccountingProvider {
  id: string;
  name: string;
  type: 'quickbooks' | 'xero' | 'freshbooks' | 'wave' | 'sage' | 'netsuite';
  is_active: boolean;
  sync_status: 'connected' | 'disconnected' | 'syncing' | 'error';
  last_sync: string;
  settings: {
    company_id?: string;
    base_currency: string;
    sync_frequency: number; // hours
    auto_sync_invoices: boolean;
    auto_sync_payments: boolean;
    auto_sync_customers: boolean;
    auto_sync_items: boolean;
    chart_of_accounts_mapping: Record<string, string>;
  };
  oauth_token?: string;
  oauth_refresh_token?: string;
  webhook_url?: string;
  created_at: string;
  updated_at: string;
}

export interface AccountingCustomer {
  id: string;
  provider_customer_id: string;
  organization_id?: string;
  contact_id?: string;
  name: string;
  email?: string;
  phone?: string;
  billing_address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  currency: string;
  payment_terms?: string;
  tax_exempt?: boolean;
  sync_status: 'pending' | 'synced' | 'error';
  last_synced: string;
  created_at: string;
  updated_at: string;
}

export interface AccountingInvoice {
  id: string;
  provider_invoice_id: string;
  invoice_id?: string; // Internal CRM invoice ID
  customer_id: string;
  invoice_number: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  issue_date: string;
  due_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  line_items: {
    description: string;
    quantity: number;
    unit_price: number;
    amount: number;
    tax_rate?: number;
    item_id?: string;
  }[];
  payments: {
    amount: number;
    date: string;
    method: string;
    reference?: string;
  }[];
  sync_status: 'pending' | 'synced' | 'error';
  last_synced: string;
  created_at: string;
  updated_at: string;
}

export interface AccountingItem {
  id: string;
  provider_item_id: string;
  name: string;
  description?: string;
  unit_price: number;
  currency: string;
  type: 'service' | 'inventory' | 'non_inventory';
  income_account?: string;
  expense_account?: string;
  tax_rate?: number;
  is_active: boolean;
  sync_status: 'pending' | 'synced' | 'error';
  last_synced: string;
  created_at: string;
  updated_at: string;
}

export interface SyncLog {
  id: string;
  provider_id: string;
  sync_type: 'customers' | 'invoices' | 'payments' | 'items' | 'full';
  status: 'started' | 'completed' | 'failed';
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  metadata: Record<string, any>;
}

class AccountingSyncService {
  // Provider Management
  async connectAccountingProvider(
    providerData: Omit<AccountingProvider, 'id' | 'created_at' | 'updated_at' | 'last_sync'>
  ): Promise<AccountingProvider> {
    const { data, error } = await supabase
      .from('accounting_providers')
      .insert({
        ...providerData,
        last_sync: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getAccountingProviders(): Promise<AccountingProvider[]> {
    const { data, error } = await supabase
      .from('accounting_providers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async updateAccountingProvider(
    id: string,
    updates: Partial<AccountingProvider>
  ): Promise<AccountingProvider> {
    const { data, error } = await supabase
      .from('accounting_providers')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Full Sync
  async performFullSync(providerId: string): Promise<SyncLog> {
    const syncLog = await this.createSyncLog(providerId, 'full');

    try {
      const provider = await this.getProvider(providerId);

      await this.updateProviderStatus(providerId, 'syncing');

      // Sync in order: customers, items, invoices, payments
      await this.syncCustomers(provider, syncLog.id);
      await this.syncItems(provider, syncLog.id);
      await this.syncInvoices(provider, syncLog.id);

      await this.completeSyncLog(syncLog.id, 'completed');
      await this.updateProviderStatus(providerId, 'connected');

      return await this.getSyncLog(syncLog.id);
    } catch (error) {
      logError('Full sync failed:', error);
      await this.completeSyncLog(
        syncLog.id,
        'failed',
        error instanceof Error ? error.message : 'Unknown error'
      );
      await this.updateProviderStatus(providerId, 'error');
      throw error;
    }
  }

  // Customer Sync
  async syncCustomers(provider: AccountingProvider, syncLogId?: string): Promise<void> {
    const customers = await this.fetchCustomersFromProvider(provider);
    let created = 0,
      updated = 0,
      failed = 0;

    for (const customerData of customers) {
      try {
        const existing = await this.findExistingCustomer(
          customerData.provider_customer_id,
          provider.id
        );

        if (existing) {
          await this.updateAccountingCustomer(existing.id, customerData);
          updated++;
        } else {
          await this.createAccountingCustomer(customerData);
          created++;
        }
      } catch (error) {
        logError('Failed to sync customer:', error);
        failed++;
      }
    }

    if (syncLogId) {
      await this.updateSyncLogCounts(syncLogId, customers.length, created, updated, failed);
    }
  }

  private async fetchCustomersFromProvider(
    provider: AccountingProvider
  ): Promise<Omit<AccountingCustomer, 'id' | 'created_at' | 'updated_at'>[]> {
    switch (provider.type) {
      case 'quickbooks':
        return await this.fetchQuickBooksCustomers(provider);
      case 'xero':
        return await this.fetchXeroCustomers(provider);
      case 'freshbooks':
        return await this.fetchFreshBooksCustomers(provider);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  private async fetchQuickBooksCustomers(provider: AccountingProvider): Promise<any[]> {
    // QuickBooks API integration would go here
    // For demo purposes, return mock data
    return [
      {
        provider_customer_id: `qb_customer_${Date.now()}`,
        name: 'Acme Corporation',
        email: 'billing@acme.com',
        phone: '+1-555-0123',
        currency: 'USD',
        payment_terms: 'Net 30',
        sync_status: 'synced',
        last_synced: new Date().toISOString(),
      },
    ];
  }

  private async fetchXeroCustomers(provider: AccountingProvider): Promise<any[]> {
    // Xero API integration would go here
    return [
      {
        provider_customer_id: `xero_customer_${Date.now()}`,
        name: 'Global Enterprises',
        email: 'accounts@global.com',
        currency: 'USD',
        sync_status: 'synced',
        last_synced: new Date().toISOString(),
      },
    ];
  }

  private async fetchFreshBooksCustomers(provider: AccountingProvider): Promise<any[]> {
    // FreshBooks API integration would go here
    return [];
  }

  // Invoice Sync
  async syncInvoices(provider: AccountingProvider, syncLogId?: string): Promise<void> {
    const invoices = await this.fetchInvoicesFromProvider(provider);
    let created = 0,
      updated = 0,
      failed = 0;

    for (const invoiceData of invoices) {
      try {
        const existing = await this.findExistingInvoice(
          invoiceData.provider_invoice_id,
          provider.id
        );

        if (existing) {
          await this.updateAccountingInvoice(existing.id, invoiceData);
          updated++;
        } else {
          await this.createAccountingInvoice(invoiceData);
          created++;
        }

        // Auto-link to CRM invoice if possible
        await this.linkToCrmInvoice(invoiceData);
      } catch (error) {
        logError('Failed to sync invoice:', error);
        failed++;
      }
    }

    if (syncLogId) {
      await this.updateSyncLogCounts(syncLogId, invoices.length, created, updated, failed);
    }
  }

  private async fetchInvoicesFromProvider(
    provider: AccountingProvider
  ): Promise<Omit<AccountingInvoice, 'id' | 'created_at' | 'updated_at'>[]> {
    switch (provider.type) {
      case 'quickbooks':
        return await this.fetchQuickBooksInvoices(provider);
      case 'xero':
        return await this.fetchXeroInvoices(provider);
      case 'freshbooks':
        return await this.fetchFreshBooksInvoices(provider);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  private async fetchQuickBooksInvoices(provider: AccountingProvider): Promise<any[]> {
    // QuickBooks invoice API integration
    return [
      {
        provider_invoice_id: `qb_invoice_${Date.now()}`,
        customer_id: 'qb_customer_123',
        invoice_number: 'INV-001',
        status: 'sent',
        issue_date: new Date().toISOString(),
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        subtotal: 1000,
        tax_amount: 100,
        total_amount: 1100,
        currency: 'USD',
        line_items: [
          {
            description: 'Consulting Services',
            quantity: 10,
            unit_price: 100,
            amount: 1000,
          },
        ],
        payments: [],
        sync_status: 'synced',
        last_synced: new Date().toISOString(),
      },
    ];
  }

  private async fetchXeroInvoices(provider: AccountingProvider): Promise<any[]> {
    // Xero invoice API integration
    return [];
  }

  private async fetchFreshBooksInvoices(provider: AccountingProvider): Promise<any[]> {
    // FreshBooks invoice API integration
    return [];
  }

  // Items/Products Sync
  async syncItems(provider: AccountingProvider, syncLogId?: string): Promise<void> {
    const items = await this.fetchItemsFromProvider(provider);
    let created = 0,
      updated = 0,
      failed = 0;

    for (const itemData of items) {
      try {
        const existing = await this.findExistingItem(itemData.provider_item_id, provider.id);

        if (existing) {
          await this.updateAccountingItem(existing.id, itemData);
          updated++;
        } else {
          await this.createAccountingItem(itemData);
          created++;
        }
      } catch (error) {
        logError('Failed to sync item:', error);
        failed++;
      }
    }

    if (syncLogId) {
      await this.updateSyncLogCounts(syncLogId, items.length, created, updated, failed);
    }
  }

  private async fetchItemsFromProvider(
    provider: AccountingProvider
  ): Promise<Omit<AccountingItem, 'id' | 'created_at' | 'updated_at'>[]> {
    switch (provider.type) {
      case 'quickbooks':
        return await this.fetchQuickBooksItems(provider);
      case 'xero':
        return await this.fetchXeroItems(provider);
      case 'freshbooks':
        return await this.fetchFreshBooksItems(provider);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  private async fetchQuickBooksItems(provider: AccountingProvider): Promise<any[]> {
    return [
      {
        provider_item_id: `qb_item_${Date.now()}`,
        name: 'Consulting Hour',
        description: 'Hourly consulting service',
        unit_price: 150,
        currency: 'USD',
        type: 'service',
        income_account: 'Services Revenue',
        is_active: true,
        sync_status: 'synced',
        last_synced: new Date().toISOString(),
      },
    ];
  }

  private async fetchXeroItems(provider: AccountingProvider): Promise<any[]> {
    return [];
  }

  private async fetchFreshBooksItems(provider: AccountingProvider): Promise<any[]> {
    return [];
  }

  // CRM Integration
  async pushInvoiceToAccounting(invoiceId: string, providerId?: string): Promise<void> {
    // Get CRM invoice details
    const { data: invoice } = await supabase
      .from('invoices')
      .select(
        `
        *,
        organizations(*),
        invoice_items(*)
      `
      )
      .eq('id', invoiceId)
      .single();

    if (!invoice) throw new Error('Invoice not found');

    // Get provider (use default if not specified)
    let provider: AccountingProvider;
    if (providerId) {
      provider = await this.getProvider(providerId);
    } else {
      const providers = await this.getAccountingProviders();
      provider = providers.find((p) => p.is_active) || providers[0];
      if (!provider) throw new Error('No accounting provider configured');
    }

    // Create customer if not exists
    const customerId = await this.findOrCreateCustomer(invoice.organizations, provider);

    // Create invoice in accounting system
    const accountingInvoice = await this.createInvoiceInProvider(provider, invoice, customerId);

    // Save mapping
    await this.createAccountingInvoice({
      provider_invoice_id: accountingInvoice.id,
      invoice_id: invoiceId,
      customer_id: customerId,
      invoice_number: invoice.invoice_number,
      status: accountingInvoice.status,
      issue_date: invoice.created_at,
      due_date: invoice.due_date,
      subtotal: invoice.subtotal,
      tax_amount: invoice.tax_amount || 0,
      total_amount: invoice.total_amount,
      currency: invoice.currency || 'USD',
      line_items:
        invoice.invoice_items?.map((item: any) => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.total_amount,
        })) || [],
      payments: [],
      sync_status: 'synced',
      last_synced: new Date().toISOString(),
    });
  }

  private async findOrCreateCustomer(
    organization: any,
    provider: AccountingProvider
  ): Promise<string> {
    // Try to find existing customer
    const { data: existing } = await supabase
      .from('accounting_customers')
      .select('provider_customer_id')
      .eq('organization_id', organization.id)
      .single();

    if (existing) {
      return existing.provider_customer_id;
    }

    // Create new customer
    const customerData = {
      name: organization.name,
      email: organization.email || '',
      phone: organization.phone || '',
      billing_address: {
        line1: organization.address,
        city: organization.city,
        state: organization.state,
        postal_code: organization.postal_code,
        country: organization.country,
      },
    };

    const providerCustomer = await this.createCustomerInProvider(provider, customerData);

    // Save mapping
    await this.createAccountingCustomer({
      provider_customer_id: providerCustomer.id,
      organization_id: organization.id,
      name: organization.name,
      email: organization.email,
      phone: organization.phone,
      currency: 'USD',
      sync_status: 'synced',
      last_synced: new Date().toISOString(),
    });

    return providerCustomer.id;
  }

  private async createCustomerInProvider(
    provider: AccountingProvider,
    customerData: any
  ): Promise<any> {
    switch (provider.type) {
      case 'quickbooks':
        return await this.createQuickBooksCustomer(provider, customerData);
      case 'xero':
        return await this.createXeroCustomer(provider, customerData);
      case 'freshbooks':
        return await this.createFreshBooksCustomer(provider, customerData);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  private async createQuickBooksCustomer(
    provider: AccountingProvider,
    customerData: any
  ): Promise<any> {
    // QuickBooks customer creation API
    return {
      id: `qb_customer_${Date.now()}`,
      name: customerData.name,
    };
  }

  private async createXeroCustomer(provider: AccountingProvider, customerData: any): Promise<any> {
    // Xero customer creation API
    return {
      id: `xero_customer_${Date.now()}`,
      name: customerData.name,
    };
  }

  private async createFreshBooksCustomer(
    provider: AccountingProvider,
    customerData: any
  ): Promise<any> {
    // FreshBooks customer creation API
    return {
      id: `fb_customer_${Date.now()}`,
      name: customerData.name,
    };
  }

  private async createInvoiceInProvider(
    provider: AccountingProvider,
    invoice: any,
    customerId: string
  ): Promise<any> {
    switch (provider.type) {
      case 'quickbooks':
        return await this.createQuickBooksInvoice(provider, invoice, customerId);
      case 'xero':
        return await this.createXeroInvoice(provider, invoice, customerId);
      case 'freshbooks':
        return await this.createFreshBooksInvoice(provider, invoice, customerId);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  private async createQuickBooksInvoice(
    provider: AccountingProvider,
    invoice: any,
    customerId: string
  ): Promise<any> {
    // QuickBooks invoice creation API
    return {
      id: `qb_invoice_${Date.now()}`,
      status: 'sent',
    };
  }

  private async createXeroInvoice(
    provider: AccountingProvider,
    invoice: any,
    customerId: string
  ): Promise<any> {
    // Xero invoice creation API
    return {
      id: `xero_invoice_${Date.now()}`,
      status: 'sent',
    };
  }

  private async createFreshBooksInvoice(
    provider: AccountingProvider,
    invoice: any,
    customerId: string
  ): Promise<any> {
    // FreshBooks invoice creation API
    return {
      id: `fb_invoice_${Date.now()}`,
      status: 'sent',
    };
  }

  // Payment Sync
  async syncPayment(paymentTransactionId: string): Promise<void> {
    // Get payment transaction
    const { data: payment } = await supabase
      .from('payment_transactions')
      .select(
        `
        *,
        payment_intents(invoice_id)
      `
      )
      .eq('id', paymentTransactionId)
      .single();

    if (!payment || !payment.payment_intents?.invoice_id) return;

    // Find accounting invoice
    const { data: accountingInvoice } = await supabase
      .from('accounting_invoices')
      .select('*')
      .eq('invoice_id', payment.payment_intents.invoice_id)
      .single();

    if (!accountingInvoice) return;

    // Get provider
    const providers = await this.getAccountingProviders();
    const provider = providers.find((p) => p.is_active);
    if (!provider) return;

    // Record payment in accounting system
    await this.recordPaymentInProvider(provider, accountingInvoice, payment);

    // Update accounting invoice with payment
    const updatedPayments = [
      ...accountingInvoice.payments,
      {
        amount: payment.amount,
        date: payment.processed_at || payment.created_at,
        method: payment.payment_method,
        reference: payment.provider_transaction_id,
      },
    ];

    await this.updateAccountingInvoice(accountingInvoice.id, {
      payments: updatedPayments,
      status:
        updatedPayments.reduce((sum, p) => sum + p.amount, 0) >= accountingInvoice.total_amount
          ? 'paid'
          : accountingInvoice.status,
    });
  }

  private async recordPaymentInProvider(
    provider: AccountingProvider,
    invoice: AccountingInvoice,
    payment: any
  ): Promise<void> {
    switch (provider.type) {
      case 'quickbooks':
        await this.recordQuickBooksPayment(provider, invoice, payment);
        break;
      case 'xero':
        await this.recordXeroPayment(provider, invoice, payment);
        break;
      case 'freshbooks':
        await this.recordFreshBooksPayment(provider, invoice, payment);
        break;
    }
  }

  private async recordQuickBooksPayment(
    provider: AccountingProvider,
    invoice: AccountingInvoice,
    payment: any
  ): Promise<void> {
    // QuickBooks payment recording API
  }

  private async recordXeroPayment(
    provider: AccountingProvider,
    invoice: AccountingInvoice,
    payment: any
  ): Promise<void> {
    // Xero payment recording API
  }

  private async recordFreshBooksPayment(
    provider: AccountingProvider,
    invoice: AccountingInvoice,
    payment: any
  ): Promise<void> {
    // FreshBooks payment recording API
  }

  // Utility Methods
  private async getProvider(id: string): Promise<AccountingProvider> {
    const { data, error } = await supabase
      .from('accounting_providers')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) throw new Error('Provider not found');
    return data;
  }

  private async updateProviderStatus(
    id: string,
    status: AccountingProvider['sync_status']
  ): Promise<void> {
    await supabase
      .from('accounting_providers')
      .update({
        sync_status: status,
        last_sync: new Date().toISOString(),
      })
      .eq('id', id);
  }

  private async createSyncLog(
    providerId: string,
    syncType: SyncLog['sync_type']
  ): Promise<SyncLog> {
    const { data, error } = await supabase
      .from('sync_logs')
      .insert({
        provider_id: providerId,
        sync_type: syncType,
        status: 'started',
        records_processed: 0,
        records_created: 0,
        records_updated: 0,
        records_failed: 0,
        started_at: new Date().toISOString(),
        metadata: {},
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async completeSyncLog(
    logId: string,
    status: 'completed' | 'failed',
    errorMessage?: string
  ): Promise<void> {
    await supabase
      .from('sync_logs')
      .update({
        status,
        completed_at: new Date().toISOString(),
        error_message: errorMessage,
      })
      .eq('id', logId);
  }

  private async updateSyncLogCounts(
    logId: string,
    processed: number,
    created: number,
    updated: number,
    failed: number
  ): Promise<void> {
    await supabase
      .from('sync_logs')
      .update({
        records_processed: processed,
        records_created: created,
        records_updated: updated,
        records_failed: failed,
      })
      .eq('id', logId);
  }

  private async getSyncLog(id: string): Promise<SyncLog> {
    const { data, error } = await supabase.from('sync_logs').select('*').eq('id', id).single();

    if (error) throw error;
    return data;
  }

  // Database operations
  private async findExistingCustomer(
    providerCustomerId: string,
    providerId: string
  ): Promise<AccountingCustomer | null> {
    const { data } = await supabase
      .from('accounting_customers')
      .select('*')
      .eq('provider_customer_id', providerCustomerId)
      .single();

    return data;
  }

  private async createAccountingCustomer(
    customerData: Omit<AccountingCustomer, 'id' | 'created_at' | 'updated_at'>
  ): Promise<AccountingCustomer> {
    const { data, error } = await supabase
      .from('accounting_customers')
      .insert(customerData)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async updateAccountingCustomer(
    id: string,
    updates: Partial<AccountingCustomer>
  ): Promise<AccountingCustomer> {
    const { data, error } = await supabase
      .from('accounting_customers')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async findExistingInvoice(
    providerInvoiceId: string,
    providerId: string
  ): Promise<AccountingInvoice | null> {
    const { data } = await supabase
      .from('accounting_invoices')
      .select('*')
      .eq('provider_invoice_id', providerInvoiceId)
      .single();

    return data;
  }

  private async createAccountingInvoice(
    invoiceData: Omit<AccountingInvoice, 'id' | 'created_at' | 'updated_at'>
  ): Promise<AccountingInvoice> {
    const { data, error } = await supabase
      .from('accounting_invoices')
      .insert(invoiceData)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async updateAccountingInvoice(
    id: string,
    updates: Partial<AccountingInvoice>
  ): Promise<AccountingInvoice> {
    const { data, error } = await supabase
      .from('accounting_invoices')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async findExistingItem(
    providerItemId: string,
    providerId: string
  ): Promise<AccountingItem | null> {
    const { data } = await supabase
      .from('accounting_items')
      .select('*')
      .eq('provider_item_id', providerItemId)
      .single();

    return data;
  }

  private async createAccountingItem(
    itemData: Omit<AccountingItem, 'id' | 'created_at' | 'updated_at'>
  ): Promise<AccountingItem> {
    const { data, error } = await supabase
      .from('accounting_items')
      .insert(itemData)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async updateAccountingItem(
    id: string,
    updates: Partial<AccountingItem>
  ): Promise<AccountingItem> {
    const { data, error } = await supabase
      .from('accounting_items')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async linkToCrmInvoice(accountingInvoice: Partial<AccountingInvoice>): Promise<void> {
    // Try to match by invoice number or customer
    const { data: crmInvoice } = await supabase
      .from('invoices')
      .select('id')
      .eq('invoice_number', accountingInvoice.invoice_number)
      .single();

    if (crmInvoice) {
      await supabase
        .from('accounting_invoices')
        .update({ invoice_id: crmInvoice.id })
        .eq('provider_invoice_id', accountingInvoice.provider_invoice_id);
    }
  }

  // Analytics
  async getSyncAnalytics(providerId?: string): Promise<any> {
    let query = supabase.from('sync_logs').select('*');

    if (providerId) {
      query = query.eq('provider_id', providerId);
    }

    const { data: logs } = await query.order('started_at', { ascending: false }).limit(100);

    if (!logs) return null;

    const completedSyncs = logs.filter((l) => l.status === 'completed');
    const failedSyncs = logs.filter((l) => l.status === 'failed');

    return {
      total_syncs: logs.length,
      successful_syncs: completedSyncs.length,
      failed_syncs: failedSyncs.length,
      success_rate: logs.length > 0 ? Math.round((completedSyncs.length / logs.length) * 100) : 0,
      total_records_synced: completedSyncs.reduce((sum, log) => sum + log.records_processed, 0),
      avg_sync_time: this.calculateAverageSyncTime(completedSyncs),
      last_sync: logs[0]?.started_at,
      sync_frequency: this.calculateSyncFrequency(logs),
    };
  }

  private calculateAverageSyncTime(logs: SyncLog[]): number {
    const completedLogs = logs.filter((l) => l.completed_at);
    if (completedLogs.length === 0) return 0;

    const totalTime = completedLogs.reduce((sum, log) => {
      const start = new Date(log.started_at).getTime();
      const end = new Date(log.completed_at!).getTime();
      return sum + (end - start);
    }, 0);

    return Math.round(totalTime / completedLogs.length / 1000); // Return in seconds
  }

  private calculateSyncFrequency(logs: SyncLog[]): number {
    if (logs.length < 2) return 0;

    const intervals = [];
    for (let i = 1; i < logs.length; i++) {
      const current = new Date(logs[i - 1].started_at).getTime();
      const previous = new Date(logs[i].started_at).getTime();
      intervals.push(current - previous);
    }

    const avgInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    return Math.round(avgInterval / (1000 * 60 * 60)); // Return in hours
  }
}

export const accountingSyncService = new AccountingSyncService();
